import { Head } from "vite-react-ssg";
import { Link, Navigate, useParams } from "react-router-dom";
import { Section, Pill } from "@/components/kit";
import { Card } from "@/components/Card";
import { CtaBand } from "@/components/CtaBand";
import { DEMO_DOMAINS } from "@/content/demos";
import { DEMO_CATALOG } from "@/content/demoCatalog.generated";

export default function DemoCategory() {
  const { category = "" } = useParams();
  const domain = DEMO_DOMAINS.find((d) => d.slug === category);
  const catalog = DEMO_CATALOG[category];

  // Unknown slug → back to the demos index.
  if (!domain || !catalog) return <Navigate to="/demos" replace />;

  return (
    <>
      <Head>
        <title>{`${domain.name} demos — DjiniousLab`}</title>
        <meta name="description" content={catalog.intro} />
      </Head>

      <Section wash="wash" containerClassName="py-14">
        <Link
          to="/demos"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          <span aria-hidden="true">←</span> All demos
        </Link>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            {domain.name}
          </h1>
          <Pill tone={domain.tone}>{domain.count} demos</Pill>
        </div>
        <p className="mt-3 max-w-2xl text-lg leading-relaxed text-muted-foreground">
          {catalog.intro}
        </p>
      </Section>

      {/* Highlights — screenshot + description cards */}
      {catalog.highlights.length > 0 && (
        <Section wash="surface" containerClassName="py-12">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {catalog.highlights.map((demo) => (
              <Card key={demo.slug} className="flex flex-col gap-4">
                <figure className="-mx-6 -mt-6 mb-1 overflow-hidden rounded-t-2xl border-b border-border bg-card">
                  <img
                    src={demo.image}
                    alt={demo.imageAlt}
                    className="block aspect-[3/2] w-full bg-wash object-cover object-top"
                    loading="lazy"
                  />
                </figure>
                <h3 className="text-lg font-extrabold leading-snug text-foreground">
                  {demo.title}
                </h3>
                <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
                  {demo.description}
                </p>
              </Card>
            ))}
          </div>
        </Section>
      )}

      {/* The rest — text list */}
      {catalog.rest.length > 0 && (
        <Section wash="plain" containerClassName="py-12">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            More in this category
          </h2>
          <ul className="mt-5 flex flex-wrap gap-2">
            {catalog.rest.map((title) => (
              <li
                key={title}
                className="rounded-full border border-border bg-card px-3 py-1 text-sm text-foreground"
              >
                {title}
              </li>
            ))}
          </ul>
        </Section>
      )}

      <CtaBand />
    </>
  );
}
